


































































































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import KesadaranGCSInput from "@/components/KesadaranGCSInput.vue";
import storeRefAsesmen, {
  Category
} from "@/store/modules/new/ReferensiAsesmen";
import IndikatorNyeri from "@/components/nyeri/IndikatorNyeri.vue";
import {
  bandNyeri,
  judulBandNyeri,
  warnaBandNyeri,
  kodeMetodeNyeri
} from "@/utils/nyeriIntervensi";

/**
 * Satu butir penilaian instrumen berskor. Label (`ref_group_title`/
 * `ref_title`) ikut disimpan ke JSON dengan sengaja: blade PDF berjalan di
 * server dan tidak bisa memanggil katalog, jadi cetakan tetap benar walau
 * master `ref_general` berubah kemudian.
 */
interface NyeriKomponen {
  ref_group_id: number;
  ref_group_title: string;
  ref_id: string | null;
  ref_title: string | null;
  skor: number | null;
}

/** Instrumen yang skornya dijumlahkan dari komponen (bukan diketik). */
const NYERI_METODE_KOMPONEN = ["flacc", "nips", "bps"];

/**
 * Instrumen yang ditawarkan — sama dengan form Asesmen Nyeri mandiri dan
 * Asesmen Awal Keperawatan Ranap Dewasa.
 *
 * Nilai di sini **huruf kecil**, mengikuti apa yang sudah tersimpan pada
 * record form ini sejak awal (`"nrs"`, `"bps"`). JANGAN diubah ke label
 * huruf besar seperti form dewasa — record lama akan berhenti cocok.
 * `kodeMetodeNyeri()` menerima kedua bentuk.
 *
 * "CPOT" & "Wong-Baker" dilepas dari daftar: keduanya tidak punya katalog
 * komponen, ambang kategori, maupun protokol tatalaksana di blanko
 * `RM.RI.19.13.02` (blanko itu menyatakan FLACC/CPOT "tidak tercakup di
 * formulir ini"). Untuk pasien terintubasi, blanko Intensif sendiri
 * menyebut BPS sebagai alternatif sah CPOT.
 */
const NYERI_METODE_BASE = ["nrs", ...NYERI_METODE_KOMPONEN];

const NYERI_METODE_LABEL: Record<string, string> = {
  nrs: "NRS",
  flacc: "FLACC",
  nips: "NIPS",
  bps: "BPS",
  cpot: "CPOT",
  wong_baker: "Wong-Baker"
};

const NYERI_METODE_KETERANGAN: Record<string, string> = {
  nrs: "Pasien sadar dan mampu melaporkan nyeri secara verbal.",
  flacc: "Pasien tidak kooperatif / tersedasi (mis. anak di PICU).",
  nips: "Neonatus 0–28 hari (NICU).",
  bps: "Pasien terintubasi / tersedasi dalam."
};

// Konstanta modul, BUKAN literal di dalam getter/method: `:items` yang
// identitasnya berubah tiap render mengacaukan watcher internal Vuetify.
const NYERI_KATALOG_KOSONG: Category[] = [];
const NYERI_OPSI_KOSONG: Category["val"] = [];

@Component({ components: { KesadaranGCSInput, IndikatorNyeri } })
export default class DisabilitySection extends Vue {
  @Prop({ default: () => ({}) }) readonly value!: any;
  @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;

  form: any = {
    kesadaran: "cm",
    gcs_e: null,
    gcs_m: null,
    gcs_v: null,
    gcs_total: null,
    pupil_kanan: null,
    pupil_kiri: null,
    respon_cahaya_kanan: "",
    respon_cahaya_kiri: "",
    nyeri_penyebab: "",
    nyeri_skala: 0,
    nyeri_alat_ukur: "nrs",
    // Kedua key di bawah WAJIB ada di literal default ini, bukan ditambahkan
    // belakangan: di Vue 2 properti yang muncul setelah objeknya reaktif
    // tidak ikut terpantau, dan input komponennya akan mati tanpa error.
    nyeri_komponen: [] as NyeriKomponen[],
    nyeri_interpretasi: null as string | null,
    nyeri_kualitas: "",
    nyeri_lokasi: "",
    nyeri_pola: ""
  };

  responOptions = ["reaktif", "tidak reaktif", "sluggish"];

  /**
   * Menahan watcher instrumen selama hidrasi. `@Watch` sudah aktif sebelum
   * `created()` berjalan, jadi tanpa flag ini record ber-`"bps"` akan
   * memicu `onAlatUkurChange` dan menghapus skor yang baru saja dimuat.
   */
  hydratingNyeri = false;
  nyeriSelectProps = { openOnHover: true, offsetY: true };
  nyeriRangeSkala = ["0", 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  get gcsKritis(): boolean {
    return typeof this.form.gcs_total === "number" && this.form.gcs_total > 0 && this.form.gcs_total <= 8;
  }

  onChangeGCS(val: { e: number; v: number; m: number }) {
    this.form.gcs_e = val.e;
    this.form.gcs_v = val.v;
    this.form.gcs_m = val.m;
    const e = Number(val.e);
    const v = Number(val.v);
    const m = Number(val.m);
    if (Number.isFinite(e) && Number.isFinite(v) && Number.isFinite(m) && e > 0 && v > 0 && m > 0) {
      this.form.gcs_total = e + v + m;
    } else {
      this.form.gcs_total = null;
    }
  }

  // ── Nyeri ───────────────────────────────────────────────────────────
  //
  // Kategori memakai **ambang per instrumen** (NRS/FLACC 0–10, NIPS 0–7,
  // BPS 3–12) lewat `utils/nyeriIntervensi` — sumber yang sama dengan form
  // Asesmen Nyeri mandiri dan Asesmen Awal Keperawatan Ranap Dewasa.

  get nyeriKode() {
    return kodeMetodeNyeri(this.form.nyeri_alat_ukur);
  }

  /**
   * Band sengaja di-gate pada instrumen yang dikenal. `bandNyeri()` punya
   * fallback ambang NRS untuk instrumen asing — itu benar di form dewasa
   * (semua berskala 0–10), tetapi CPOT berskala 0–8 sehingga label NRS akan
   * salah. Record lama CPOT/Wong-Baker karenanya tampil tanpa kategori;
   * tidak ada yang hilang, form ini memang belum pernah menampilkannya.
   */
  get nyeriBand() {
    return this.nyeriKode
      ? bandNyeri(this.nyeriKode, this.form.nyeri_skala)
      : null;
  }
  get nyeriInterpretasi() {
    return judulBandNyeri(this.nyeriBand);
  }
  get nyeriColor() {
    return warnaBandNyeri(this.nyeriBand);
  }

  get nyeriAlatText(): string {
    const k = String(this.form.nyeri_alat_ukur || "");
    return NYERI_METODE_LABEL[k] || k.toUpperCase();
  }

  /**
   * Nilai lama di luar basis — mis. `"cpot"` / `"wong_baker"` pada record
   * sebelum perubahan ini — tetap disertakan agar record lama tidak berubah
   * isinya hanya karena dibuka kembali.
   *
   * WAJIB computed (ter-cache), bukan method: `:items` dari method membangun
   * array baru tiap render dan mengacaukan watcher internal Vuetify.
   */
  get nyeriMethodItems(): Array<{ text: string; value: string }> {
    const cur = String(this.form.nyeri_alat_ukur || "");
    const list =
      cur && NYERI_METODE_BASE.indexOf(cur) === -1
        ? [...NYERI_METODE_BASE, cur]
        : NYERI_METODE_BASE;
    return list.map(v => ({
      value: v,
      text: NYERI_METODE_LABEL[v] || v.toUpperCase()
    }));
  }

  get nyeriMethodKeterangan(): string {
    return (
      NYERI_METODE_KETERANGAN[String(this.form.nyeri_alat_ukur)] ||
      "Instrumen lama — tetap ditampilkan agar isi record tidak berubah; tidak ada katalog komponen maupun protokol tatalaksana baku untuknya."
    );
  }

  get nyeriPakaiKomponen(): boolean {
    return (
      NYERI_METODE_KOMPONEN.indexOf(String(this.form.nyeri_alat_ukur)) !== -1
    );
  }

  /** Katalog komponen instrumen terpilih; datang dari master `ref_general`. */
  get nyeriKomponenAktif(): Category[] {
    switch (String(this.form.nyeri_alat_ukur)) {
      case "flacc":
        return storeRefAsesmen.nyeriFLACC;
      case "nips":
        return storeRefAsesmen.nyeriNIPS;
      case "bps":
        return storeRefAsesmen.nyeriBPS;
      default:
        return NYERI_KATALOG_KOSONG;
    }
  }

  /**
   * Opsi untuk satu komponen. Mengembalikan referensi array milik store apa
   * adanya (bukan salinan) supaya identitas `:items` stabil antar render.
   */
  nyeriOpsiKomponen(refGroupId: number) {
    // Koersi eksplisit: `ref_group_id` hasil JSON.parse bisa datang sebagai
    // string pada record lama, sedangkan `Category.id` dari API selalu angka.
    const cat = this.nyeriKomponenAktif.find(
      c => Number(c.id) === Number(refGroupId)
    );
    return cat ? cat.val : NYERI_OPSI_KOSONG;
  }

  /** Semua komponen instrumen sudah dinilai (bukan sekadar sebagian). */
  get nyeriKomponenLengkap(): boolean {
    const items = this.form.nyeri_komponen as NyeriKomponen[];
    return (
      Array.isArray(items) && items.length > 0 && items.every(i => i.skor != null)
    );
  }

  /**
   * Total = jumlah skor seluruh komponen, dan hanya bila SEMUA komponen
   * sudah terisi — aturan yang sama dengan form Asesmen Nyeri mandiri. Skor
   * parsial menyesatkan secara klinis, jadi dikembalikan `null`.
   */
  get nyeriTotalKomponen(): number | null {
    if (!this.nyeriKomponenLengkap) return null;
    return (this.form.nyeri_komponen as NyeriKomponen[]).reduce(
      (sum, i) => sum + (i.skor || 0),
      0
    );
  }

  /** Bangun kerangka komponen dari katalog, menyalin nilai lama bila cocok. */
  buildNyeriKomponen(sebelumnya: NyeriKomponen[] = []): NyeriKomponen[] {
    return this.nyeriKomponenAktif.map(cat => {
      const lama = (sebelumnya || []).find(
        k => k && Number(k.ref_group_id) === Number(cat.id)
      );
      return {
        ref_group_id: cat.id,
        ref_group_title: cat.title,
        ref_id: lama ? lama.ref_id : null,
        ref_title: lama ? lama.ref_title : null,
        skor: lama && lama.skor != null ? Number(lama.skor) : null
      };
    });
  }

  setNyeriKomponen(idx: number, refId: string | null) {
    const row = (this.form.nyeri_komponen as NyeriKomponen[])[idx];
    if (!row) return;
    const opsi = this.nyeriOpsiKomponen(row.ref_group_id).find(
      o => String(o.id) === String(refId)
    );
    // Semua key sudah ada sejak buildNyeriKomponen() → mutasi properti objek
    // di dalam array tetap reaktif di Vue 2.
    row.ref_id = opsi ? opsi.id : null;
    row.ref_title = opsi ? opsi.title : null;
    row.skor = opsi ? Number(opsi.scoring) : null;
    this.form.nyeri_skala = this.nyeriTotalKomponen;
  }

  /**
   * `v-slider` tidak pernah meng-emit saat mount: thumb sudah di posisi 0,
   * jadi perawat yang memang menilai **0** tak punya cara menghasilkan event
   * dan skornya akan tersimpan `null` ("belum dinilai") — beda arti.
   */
  seedSkalaSlider() {
    if (this.nyeriPakaiKomponen) return;
    if (this.form.nyeri_skala == null) this.form.nyeri_skala = 0;
  }

  /**
   * Selaraskan array komponen dengan katalog instrumen terpilih.
   * `reset` = true saat pengguna berganti instrumen (nilai lama dibuang);
   * false saat hidrasi / katalog baru selesai diunduh (nilai tersimpan dijaga).
   */
  syncNyeriKomponen(reset = false) {
    if (!this.nyeriPakaiKomponen) {
      this.form.nyeri_komponen = [];
      return;
    }
    if (!this.nyeriKomponenAktif.length) return; // katalog belum siap
    const lama = reset ? [] : (this.form.nyeri_komponen as NyeriKomponen[]);
    // Sengaja TIDAK menyentuh `nyeri_skala` di sini. Fungsi ini hanya
    // menyusun kerangka komponen — dipanggil juga saat katalog baru tiba
    // pada record lama yang skornya diketik manual; menghitung ulang di situ
    // akan menghapus isi rekam medis yang sah. Skor hanya berubah oleh aksi
    // pengguna (`setNyeriKomponen`) atau saat instrumen diganti.
    this.form.nyeri_komponen = this.buildNyeriKomponen(lama);
  }

  /**
   * Snapshot label kategori ke JSON, mengikuti pola `morse`/`braden`/
   * `gizi_mst` pada form dewasa: blade PDF tinggal membacanya tanpa
   * menghitung ulang.
   */
  applyInterpretasi() {
    this.form.nyeri_interpretasi = this.nyeriBand
      ? judulBandNyeri(this.nyeriBand)
      : null;
  }

  /**
   * Jaring pengaman. Katalog sudah dimuat halaman induk
   * (`PemeriksaanRanap.callDataStore()`), tetapi section ini lazy-mount dan
   * bisa dipakai di konteks lain; guard `.length` membuatnya idempoten.
   */
  loadKatalogNyeri() {
    if (!storeRefAsesmen.nyeriFLACC.length) storeRefAsesmen.getNyeriFLACC();
    if (!storeRefAsesmen.nyeriNIPS.length) storeRefAsesmen.getNyeriNIPS();
    if (!storeRefAsesmen.nyeriBPS.length) storeRefAsesmen.getNyeriBPS();
  }

  created() {
    // WAJIB baris pertama — lihat komentar `hydratingNyeri`.
    this.hydratingNyeri = true;
    if (this.value && typeof this.value === "object") {
      this.form = { ...this.form, ...this.value };
    }
    if (!Array.isArray(this.form.nyeri_komponen)) this.form.nyeri_komponen = [];
    this.loadKatalogNyeri();
    this.syncNyeriKomponen(false); // no-op bila katalog belum tiba
    this.applyInterpretasi();
    // `$nextTick` berjalan setelah `flushSchedulerQueue`, jadi seluruh
    // callback watcher di atas pasti masih melihat flag bernilai true.
    this.$nextTick(() => {
      this.hydratingNyeri = false;
    });
  }

  @Watch("form.nyeri_alat_ukur")
  onAlatUkurChange(val: string, oldVal: string) {
    if (this.hydratingNyeri || val === oldVal) return;
    // Skor lama milik instrumen lain — jangan dibawa ke instrumen baru.
    this.form.nyeri_skala = null;
    this.syncNyeriKomponen(true);
    this.seedSkalaSlider();
  }

  /**
   * Katalog diunduh asinkron. Bila section ter-mount lebih dulu, array
   * `nyeri_komponen` masih kosong — bangun begitu katalognya tiba. Sengaja
   * TIDAK digerbangi `hydratingNyeri`: justru saat hidrasi itulah kerangka
   * komponen perlu disusun bila katalog datang belakangan.
   */
  @Watch("nyeriKomponenAktif")
  onKatalogReady() {
    if (!this.nyeriPakaiKomponen) return;
    if ((this.form.nyeri_komponen as NyeriKomponen[]).length) return;
    this.syncNyeriKomponen(false);
  }

  @Watch("nyeriBand")
  onBandChange() {
    this.applyInterpretasi();
  }

  @Watch("form", { deep: true })
  onFormChange(val: any) { this.$emit("input", { ...val }); }

  syncFromValue(val: any) {
    if (!val) return;
    this.hydratingNyeri = true;
    this.form = { ...this.form, ...val };
    if (!Array.isArray(this.form.nyeri_komponen)) this.form.nyeri_komponen = [];
    this.syncNyeriKomponen(false);
    this.applyInterpretasi();
    this.$nextTick(() => {
      this.hydratingNyeri = false;
    });
  }
}
